/**
 * AdminGlossaryPage — super-admin curated AI Report Builder vocabulary.
 *
 * The semantic-layer glossary that teaches Claude how to map admin
 * phrasing onto the right entity/measure used to live as a hard-coded
 * blob in the backend (mc_semantic.schema_for_llm). Every new term
 * needed a redeploy. This page lifts that into a Mongo collection so
 * super-admins can:
 *   • register synonyms ("body count" → users.count)
 *   • override ambiguous mappings on the fly
 *   • PROBE a term through the live LLM planner to verify what
 *     Claude picks before committing
 *
 * UX notes
 * ────────
 * • Single-column list + side editor pattern (no modal — admins
 *   often want to scan the list while editing).
 * • Aliases are comma-separated; we chip-ify on render but keep the
 *   raw editing UX a single textarea so power users can paste.
 * • Entity picker is sourced from /api/mc/reports/catalog so it's
 *   always in sync with the live schema; measure picker filters to
 *   the selected entity's measures.
 * • "Probe term" runs against the live planner and shows the chosen
 *   {entity, measure, group_by} — green when it matches the
 *   editor's mapping, amber when it diverges.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Plus,
  Play,
  Save,
  Trash2,
  Wand2,
  CheckCircle2,
  AlertTriangle,
  X as XIcon,
  Search,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { getLightSectionTheme } from '../../lib/sectionThemes';

const EMPTY_DRAFT = {
  id: null,
  term: '',
  aliases: '',
  entity: '',
  measure: '',
  notes: '',
};

function aliasesArray(raw) {
  return (raw || '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function MappingChip({ entity, measure }) {
  if (!entity) return <span className="text-xs text-slate-400 italic">No mapping</span>;
  return (
    <span className="text-xs font-mono text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">
      {entity}{measure ? `.${measure}` : ''}
    </span>
  );
}

function ProbeResult({ probe, expectedEntity, expectedMeasure }) {
  if (!probe) return null;
  if (!probe.ok) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2"
        data-testid="glossary-probe-result">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Claude couldn’t route this term</div>
          <div className="mt-0.5">{probe.hint || 'Try rephrasing or adding clearer aliases.'}</div>
        </div>
      </div>
    );
  }
  const pickedEntity = probe.entity;
  const matches =
    expectedEntity
      ? pickedEntity === expectedEntity
        && (!expectedMeasure
          || (probe.measures || []).some((m) => m.id === expectedMeasure))
      : true;
  const tone = matches
    ? { wrap: 'border-emerald-200 bg-emerald-50', icon: 'text-emerald-600', heading: 'text-emerald-900' }
    : { wrap: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', heading: 'text-amber-900' };
  return (
    <div className={`rounded-lg border p-3 text-xs flex items-start gap-2 ${tone.wrap}`}
      data-testid="glossary-probe-result">
      {matches ? (
        <CheckCircle2 className={`h-4 w-4 flex-shrink-0 mt-0.5 ${tone.icon}`} />
      ) : (
        <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${tone.icon}`} />
      )}
      <div className="flex-1 min-w-0">
        <div className={`font-semibold ${tone.heading}`}>
          {matches ? 'Claude routed as expected' : 'Claude picked a different mapping'}
        </div>
        <div className="mt-1 text-slate-700 space-y-0.5">
          {probe.title ? <div className="italic">“{probe.title}”</div> : null}
          <div>
            <span className="text-slate-500">action:</span>{' '}
            <code className="font-mono">{probe.action || '—'}</code>
          </div>
          <div>
            <span className="text-slate-500">entity:</span>{' '}
            <code className="font-mono">{pickedEntity || '—'}</code>
          </div>
          {probe.measures?.length ? (
            <div>
              <span className="text-slate-500">measures:</span>{' '}
              <code className="font-mono">
                {(probe.measures || []).map((m) => m.id).join(', ')}
              </code>
            </div>
          ) : null}
          {probe.group_by?.length ? (
            <div>
              <span className="text-slate-500">group_by:</span>{' '}
              <code className="font-mono">{(probe.group_by || []).join(', ')}</code>
            </div>
          ) : null}
          {probe.clarify_question ? (
            <div className="text-amber-900 italic">
              Claude asked: “{probe.clarify_question}”
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminGlossaryPage() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [entities, setEntities] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState(null);
  const [query, setQuery] = useState('');

  const heroTheme = getLightSectionTheme('admin');
  const isEditing = !!draft.id;

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get('/mc/glossary/list');
      setTerms(data?.terms || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load glossary');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    try {
      const { data } = await api.get('/mc/reports/catalog');
      setEntities(data?.entities || []);
    } catch {
      /* decorative — silently ignore */
    }
  }, []);

  useEffect(() => { fetchList(); fetchCatalog(); }, [fetchList, fetchCatalog]);

  // Measure dropdown depends on the chosen entity. Memoize to avoid
  // re-walking the catalog on every keystroke.
  const measuresForEntity = useMemo(() => {
    if (!draft.entity) return [];
    const ent = entities.find((e) => e.id === draft.entity);
    return ent?.measures || [];
  }, [entities, draft.entity]);

  // Reset the measure if it doesn't exist on the newly-picked entity.
  useEffect(() => {
    if (!draft.measure) return;
    if (measuresForEntity.find((m) => m.id === draft.measure)) return;
    setDraft((d) => ({ ...d, measure: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.entity]);

  const filteredTerms = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return terms;
    return terms.filter((t) => {
      const hay = [
        t.term,
        t.entity,
        t.measure,
        ...(t.aliases || []),
        t.notes || '',
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [terms, query]);

  const startNew = () => {
    setDraft(EMPTY_DRAFT);
    setProbe(null);
  };

  const startEdit = (t) => {
    setDraft({
      id: t.id,
      term: t.term || '',
      aliases: (t.aliases || []).join(', '),
      entity: t.entity || '',
      measure: t.measure || '',
      notes: t.notes || '',
    });
    setProbe(null);
  };

  const onSave = async () => {
    const term = draft.term.trim();
    if (!term) {
      toast.error('Term is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        term,
        aliases: aliasesArray(draft.aliases),
        entity: draft.entity || null,
        measure: draft.measure || null,
        notes: draft.notes.trim() || null,
      };
      if (draft.id) {
        const { data } = await api.put(`/mc/glossary/${draft.id}`, payload);
        setTerms((ts) => ts.map((t) => (t.id === draft.id ? data : t)));
        toast.success('Term updated');
      } else {
        const { data } = await api.post('/mc/glossary', payload);
        setTerms((ts) => [data, ...ts]);
        setDraft({ ...EMPTY_DRAFT, ...{
          id: data.id,
          term: data.term,
          aliases: (data.aliases || []).join(', '),
          entity: data.entity || '',
          measure: data.measure || '',
          notes: data.notes || '',
        } });
        toast.success('Term added');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save term');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Remove this term from the glossary?')) return;
    try {
      await api.delete(`/mc/glossary/${id}`);
      setTerms((ts) => ts.filter((t) => t.id !== id));
      if (draft.id === id) startNew();
      toast.success('Removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove');
    }
  };

  const onProbe = async () => {
    const term = draft.term.trim();
    if (!term) {
      toast.error('Type a term first');
      return;
    }
    setProbing(true);
    setProbe(null);
    try {
      const { data } = await api.post('/mc/glossary/probe', { term });
      setProbe(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Probe failed');
    } finally {
      setProbing(false);
    }
  };

  /**
   * "Show me an example chart" — jump into the AI Report Builder
   * with a one-shot prompt that exercises this exact glossary term.
   *
   * Builder strategy: lean on the term + a sensible default
   * verb so Claude has enough signal to pick the right entity.
   * We pass aliases too — they're the natural-language synonyms
   * admins actually use — which makes the demo feel less canned.
   */
  const onShowExample = (t) => {
    const phrase = t.term.trim();
    const alias = (t.aliases || [])[0];
    // Build a prompt that maps cleanly to Claude's planner. Group-by
    // org is the safest universal default (works for every entity
    // that has organization_id, which is most of them).
    let prompt;
    if (alias) {
      prompt = `Show me ${phrase} (also known as ${alias}) broken down by organization`;
    } else {
      prompt = `Show me ${phrase} broken down by organization`;
    }
    navigate(`/admin/reports/builder?prompt=${encodeURIComponent(prompt)}`);
  };

  return (
    <PageShell
      theme={heroTheme}
      hero={(
        <PageHero
          title={(
            <>
              <span className="text-[#394fd4]">AI Glossary</span>
              <span className="text-slate-900"> — Teach Claude your vocabulary</span>
            </>
          )}
          description="Map the phrases your admins actually say onto the right semantic entity + measure. Changes go live on the next AI Report Builder turn — no deploy needed."
          theme={heroTheme}
          testId="admin-glossary-hero"
          actions={(
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={startNew}
                className="gap-1.5 bg-[#394fd4] hover:bg-[#2f43b8] text-white"
                data-testid="glossary-new-btn"
              >
                <Plus className="h-3.5 w-3.5" />
                New term
              </Button>
            </div>
          )}
        />
      )}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6"
        data-testid="admin-glossary-page">
        {/* ── Term list ─────────────────────────────────────── */}
        <section className="min-w-0 space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search terms, aliases, entities…"
              className="pl-9"
              data-testid="glossary-search"
            />
          </div>
          <div className="text-xs text-slate-500">
            {loadingList
              ? 'Loading…'
              : `${filteredTerms.length} of ${terms.length} term${terms.length === 1 ? '' : 's'}`}
          </div>
          {loadingList ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center text-sm text-slate-500 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading glossary…
            </div>
          ) : filteredTerms.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center"
              data-testid="glossary-empty">
              <BookOpen className="h-8 w-8 mx-auto mb-3 text-slate-400" />
              <div className="text-sm text-slate-700 font-medium">
                {terms.length === 0 ? 'No glossary terms yet.' : 'No matches.'}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {terms.length === 0
                  ? 'Add the first synonym — e.g. "body count" → users.count.'
                  : 'Try a different keyword or clear the search.'}
              </div>
            </div>
          ) : (
            <ul className="space-y-2">
              {filteredTerms.map((t) => (
                <li key={t.id}>
                  <article
                    className={`rounded-xl border bg-white p-4 hover:border-[#394fd4]/40 transition-colors cursor-pointer ${draft.id === t.id ? 'border-[#394fd4] ring-2 ring-[#394fd4]/20' : 'border-slate-200'}`}
                    onClick={() => startEdit(t)}
                    data-testid={`glossary-row-${t.id}`}
                  >
                    <header className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-slate-900">{t.term}</div>
                        {t.aliases?.length ? (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {t.aliases.slice(0, 8).map((a) => (
                              <Badge key={a} variant="secondary"
                                className="text-[10px] font-normal bg-slate-100 text-slate-700 hover:bg-slate-100">
                                {a}
                              </Badge>
                            ))}
                            {t.aliases.length > 8 ? (
                              <span className="text-[10px] text-slate-400">
                                +{t.aliases.length - 8}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        <MappingChip entity={t.entity} measure={t.measure} />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onShowExample(t); }}
                          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 text-[10px] font-medium text-indigo-700 hover:bg-indigo-50"
                          title="Open in the AI Report Builder with a sample prompt"
                          data-testid={`glossary-example-${t.id}`}
                        >
                          <Play className="h-3 w-3" />
                          Example
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDelete(t.id); }}
                          className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          aria-label="Remove"
                          data-testid={`glossary-delete-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </header>
                    {t.notes ? (
                      <p className="text-xs text-slate-600 mt-2 line-clamp-2">{t.notes}</p>
                    ) : null}
                  </article>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── Editor ────────────────────────────────────────── */}
        <aside className="lg:sticky lg:top-4 space-y-3"
          data-testid="glossary-editor">
          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
            <header className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-slate-900">
                {isEditing ? 'Edit term' : 'New term'}
              </h2>
              {isEditing ? (
                <button
                  type="button"
                  onClick={startNew}
                  className="text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                  data-testid="glossary-clear-draft"
                >
                  <XIcon className="h-3 w-3" />
                  Clear
                </button>
              ) : null}
            </header>

            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                Term *
              </label>
              <Input
                value={draft.term}
                onChange={(e) => setDraft((d) => ({ ...d, term: e.target.value }))}
                placeholder="e.g. Headcount"
                data-testid="glossary-input-term"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                Aliases <span className="text-slate-400 font-normal">(comma-separated)</span>
              </label>
              <Textarea
                value={draft.aliases}
                onChange={(e) => setDraft((d) => ({ ...d, aliases: e.target.value }))}
                placeholder="body count, ftes, total people"
                rows={2}
                className="resize-none text-sm"
                data-testid="glossary-input-aliases"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                  Entity
                </label>
                <select
                  value={draft.entity}
                  onChange={(e) => setDraft((d) => ({ ...d, entity: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#394fd4]/30"
                  data-testid="glossary-select-entity"
                >
                  <option value="">— none —</option>
                  {entities.map((e) => (
                    <option key={e.id} value={e.id}>{e.label} ({e.id})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                  Measure
                </label>
                <select
                  value={draft.measure}
                  onChange={(e) => setDraft((d) => ({ ...d, measure: e.target.value }))}
                  disabled={!draft.entity}
                  className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#394fd4]/30 disabled:bg-slate-50 disabled:text-slate-400"
                  data-testid="glossary-select-measure"
                >
                  <option value="">— none —</option>
                  {measuresForEntity.map((m) => (
                    <option key={m.id} value={m.id}>{m.label || m.id} ({m.agg})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                Notes for Claude
              </label>
              <Textarea
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                placeholder="When to pick this — e.g. 'Use users.count for total headcount; group by department for breakdowns.'"
                rows={3}
                className="resize-none text-sm"
                data-testid="glossary-input-notes"
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button
                onClick={onSave}
                disabled={saving || !draft.term.trim()}
                size="sm"
                className="gap-1.5 bg-[#394fd4] hover:bg-[#2f43b8] text-white flex-1"
                data-testid="glossary-save-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                {isEditing ? 'Save changes' : 'Add to glossary'}
              </Button>
              <Button
                onClick={onProbe}
                disabled={probing || !draft.term.trim()}
                variant="outline"
                size="sm"
                className="gap-1.5"
                data-testid="glossary-probe-btn"
                title="Run this term through the live LLM planner to see what Claude picks"
              >
                {probing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                Probe
              </Button>
            </div>

            <ProbeResult
              probe={probe}
              expectedEntity={draft.entity}
              expectedMeasure={draft.measure}
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-xs text-slate-600 leading-relaxed">
            <div className="font-semibold text-slate-800 mb-1">How this changes Claude</div>
            Every term you add here is appended to the AI Report Builder’s
            system prompt as <em>admin-curated vocabulary</em>. Aliases
            help Claude match the way your team naturally talks; notes
            give it disambiguation guidance. Use the <strong>Probe</strong>
            button to verify the routing before relying on the term in
            board reviews.
          </div>
        </aside>
      </div>
    </PageShell>
  );
}
